/* eslint-disable react/prop-types */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../shared/images/teambuildr-selection.json';
import { TransitionGlobal } from '../../shared/GlobalStyles';
import generateUniqueId from '../../shared/utils/generateUniqueId';

import NewQuestionForm from './components/presentational/NewQuestionForm';
import BaseModal from './modals/BaseModal';
import AddThumbnailModalContent from './modals/AddThumbnailModalContent';
import AddAssetModalContent from './modals/AddAssetModalContent';
import LoadProgramsModal from './modals/LoadProgramsModal';
import SettingsModal from './modals/SettingsModal';
import VideoModal from './modals/VideoModal';
import ShareWorkoutModal from './modals/ShareWorkoutModal';
import ItemsUseLegacyOrderInfoModal from './modals/ItemsUseLegacyOrderInfoModal';
import SaveWorkoutModal from './modals/SaveWorkoutModal';
import ClearWorkoutItemsModal from './modals/ClearWorkoutItemsModal';
import CopyWorkoutModal from './modals/CopyWorkoutModal';
import ConfirmDeleteModal from './modals/ConfirmDeleteModal';
import ConfirmResetOrderModal from './modals/ConfirmResetOrderModal';
import ConfirmDiscardPreSaveModal from './modals/ConfirmDiscardPreSaveModal';
import ConfirmUndoModal from './modals/ConfirmUndoModal';
import AboutExercisePropertiesModal from './modals/AboutExercisePropertiesModal';
import { pendingNavigationRef } from './hooks/usePreSaveNavigationGuard';

import {
  setActiveModal, setActiveSecondLayerModal, setIframelyError, setAddInfoContent, setClickedUpgradeNow, addAsset, clearNewlyCreatedQuestion, setQuestionCount,
  setExercisePickerSearchTerm, fetchCurrentUser,
} from './ducks/calendarActions';
import SelectCalendarModal from './modals/SelectCalendarModal';
import SelectProgramModal from './modals/SelectProgramModal';
import AddDescriptionModal from './modals/AddDescriptionModal';
import CreateNewComponentModal from './modals/CreateNewComponentModal';
import TagAssociatedExercisesModal from './modals/TagAssociatedExercisesModal';
import PremiumFeatureUploadVideoModal from './modals/PremiumFeatureUploadVideoModal';
import SetRecurringItemModal from './modals/SetRecurringItemModal';
import UpdateFutureOccurrencesModal from './modals/UpdateFutureOccurrencesModal';

const ModalContainer = styled('div')`
  width: 100%;
  height: ${(props) => (props.height ? props.height : 'auto')};

  .modal-button {
    position: absolute;
    bottom: 0px;
  }
  
  .toggle {
    display: flex;
    flex-direction: row;
    width: 100%;
  }

  @media (max-width: 540px) {
    padding: 0px!important;
  }

  @media screen and (max-width: 1024) {
    padding: 0px 30px 60px 30px;
  }
`;

const CalendarModalHandler = () => {
  const activeModal = useSelector((state) => state.calendar.data.activeModal);
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const iframelyError = useSelector((state) => state.calendar.data.iframelyError);
  const assetToAdd = useSelector((state) => state.calendar.data.addAsset);
  const newlyCreatedQuestion = useSelector((state) => state.calendar.data.newlyCreatedQuestion);
  const questionCount = useSelector((state) => state.calendar.data.questionCount);
  const dispatch = useDispatch();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  // State to keep track of the modal content during transition
  const [displayModal, setDisplayModal] = useState('');
  /** Set true by a child modal that is doing async work and wants to block
   * backdrop / Escape / X-button close until it finishes.
   */
  const [isCloseLocked, setIsCloseLocked] = useState(false);

  // Update displayModal when activeModal changes
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (activeModal) {
      setDisplayModal(activeModal);
    } else {
      // Small delay to keep modal content visible during close animation
      const timer = setTimeout(() => {
        setDisplayModal('');
        setIsCloseLocked(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [activeModal]);

  const closeModal = () => {
    // Add a small delay to ensure the animation transitions properly
    // This forces the browser to process the animation
    setTimeout(() => {
      // Backdrop/escape/X close of the discard-preSave confirm counts as cancel
      if (activeModal === 'confirmDiscardPreSaveModal') {
        pendingNavigationRef.current = null;
      }
      // Immediately clear the active modal and modalData in Redux
      dispatch(setActiveModal('', null));
      if (iframelyError) {
        dispatch(setIframelyError(null));
      }
      if (assetToAdd) {
        dispatch(addAsset(null));
      }
      if (newlyCreatedQuestion) {
        dispatch(clearNewlyCreatedQuestion());
      }
      if (questionCount?.length) {
        /** reset question count in redux */
        dispatch(setQuestionCount([{
          index: 0, id: `${generateUniqueId()}-null`, isNullValue: true, isVisible: true,
        }]));
      }
      dispatch(setAddInfoContent({}));
      dispatch(setExercisePickerSearchTerm(''));
    }, 10); // Small delay to ensure animation starts
  };

  const closeRecurringItemModal = () => {
    // Add a small delay to ensure the animation transitions properly
    setTimeout(() => {
      dispatch(setActiveModal(''));
    }, 10); // Small delay to ensure animation starts
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getModalContentWidth = () => {
    if (displayModal === 'createNewComponentModal') {
      return windowWidth < 600 ? '95vw' : '75vw';
    }
    if (displayModal === 'tagAssociatedExercisesModal') {
      return windowWidth < 600 ? '95vw' : '60vw';
    }
    if (displayModal === 'settingsModal') {
      return windowWidth < 600 ? '95vw' : '600px';
    }
    if (displayModal === 'videoModal') {
      return windowWidth < 600 ? '95vw' : '600px';
    }
    if (displayModal === 'copyWorkoutModal') {
      return windowWidth < 600 ? '95vw' : '60vw';
    }
    if (displayModal === 'updateFutureOccurrencesModal') {
      return windowWidth < 600 ? '95vw' : '60vw';
    }
    if (displayModal === 'saveWorkoutModal') {
      return windowWidth < 600 ? '95vw' : '500px';
    }
    if (displayModal === 'addInfoOrDescriptionModal') {
      return windowWidth < 600 ? '95vw' : '600px';
    }
    return windowWidth < 600 ? '95vw' : '60vw';
  };

  const getModalContentBorderRadius = () => {
    if (
      displayModal === 'tagAssociatedExercisesModal'
      || displayModal === 'settingsModal'
      || displayModal === 'saveWorkoutModal'
      || displayModal === 'confirmDeleteModal'
      || displayModal === 'aboutExercisePropertiesModal'
    ) {
      return '8px';
    }
    return null;
  };

  const getModalContentPadding = () => {
    if (displayModal === 'createNewComponentModal'
        || displayModal === 'loadProgramsModal'
        || displayModal === 'shareWorkoutModal'
        || displayModal === 'itemsUseLegacyOrderInfoModal'
        || displayModal === 'settingsModal'
        || displayModal === 'saveWorkoutModal'
        || displayModal === 'clearWorkoutItemsModal'
        || displayModal === 'confirmDeleteModal'
        || displayModal === 'copyWorkoutModal'
        || displayModal === 'updateFutureOccurrencesModal'
        || displayModal === 'aboutExercisePropertiesModal') {
      return '24px';
    }
    if (displayModal === 'videoModal') {
      return '0px';
    }
    if ((displayModal === 'selectCalendarModal' || displayModal === 'selectProgramModal') && windowWidth < 768) {
      return '20px';
    }
    return '40px';
  };

  const getModalContentMaxWidth = () => {
    if (displayModal === 'selectCalendarModal' || displayModal === 'selectProgramModal') {
      return '632px';
    }
    return null;
  };

  const getModalContentMinWidth = () => {
    if (displayModal === 'copyWorkoutModal') {
      return '680px';
    }
    if (displayModal === 'updateFutureOccurrencesModal') {
      return '680px';
    }
    return '330px';
  };

  return (
    <BaseModal
      onRequestClose={() => {
        if (isCloseLocked) return;
        closeModal();
      }}
      isOpen={!!activeModal}
      customButton
      sidebarWidth={0}
      width={getModalContentWidth()}
      minWidth={getModalContentMinWidth()}
      maxWidth={getModalContentMaxWidth()}
      borderRadius={getModalContentBorderRadius()}
      padding={getModalContentPadding()}
    >
      <ModalContainer
        sidebarWidth={0}
      >
        {displayModal === 'selectCalendarModal' && (
          <SelectCalendarModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'selectProgramModal' && (
          <SelectProgramModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'addInfoOrDescriptionModal' && (
          <AddDescriptionModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'setRecurringItemModal' && (
          <SetRecurringItemModal
            closeModal={closeRecurringItemModal}
            setIsCloseLocked={setIsCloseLocked}
          />
        )}
        {displayModal === 'updateFutureOccurrencesModal' && (
          <UpdateFutureOccurrencesModal
            closeModal={closeModal}
            modalData={modalData}
          />
        )}
        {displayModal === 'createNewComponentModal' && (
          <CreateNewComponentModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'tagAssociatedExercisesModal' && (
          <TagAssociatedExercisesModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'loadProgramsModal' && (
          <LoadProgramsModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'settingsModal' && (
          <SettingsModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'videoModal' && (
          <VideoModal
            url={modalData?.url}
            closeModal={closeModal}
          />
        )}
        {displayModal === 'shareWorkoutModal' && (
          <ShareWorkoutModal
            closeModal={closeModal}
            modalData={modalData}
          />
        )}
        {displayModal === 'itemsUseLegacyOrderInfoModal' && (
        <ItemsUseLegacyOrderInfoModal
          closeModal={closeModal}
        />
        )}
        {displayModal === 'saveWorkoutModal' && (
          <SaveWorkoutModal
            closeModal={closeModal}
            modalData={modalData}
          />
        )}
        {displayModal === 'clearWorkoutItemsModal' && (
          <ClearWorkoutItemsModal
            closeModal={closeModal}
            modalData={modalData}
          />
        )}
        {displayModal === 'copyWorkoutModal' && (
          <CopyWorkoutModal
            closeModal={closeModal}
            modalData={modalData}
          />
        )}
        {displayModal === 'confirmDeleteModal' && (
          <ConfirmDeleteModal
            closeModal={closeModal}
            setIsCloseLocked={setIsCloseLocked}
          />
        )}
        {displayModal === 'confirmResetOrderModal' && (
        <ConfirmResetOrderModal
          closeModal={closeModal}
        />
        )}
        {displayModal === 'confirmDiscardPreSaveModal' && (
          <ConfirmDiscardPreSaveModal
            closeModal={closeModal}
          />
        )}
        {displayModal === 'confirmUndoModal' && (
          <ConfirmUndoModal
            closeModal={closeModal}
            modalData={modalData}
          />
        )}
        {displayModal === 'aboutExercisePropertiesModal' && (
          <AboutExercisePropertiesModal
            closeModal={closeModal}
          />
        )}
      </ModalContainer>
    </BaseModal>
  );
};

const SecondLayerModalOverlay = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  /* Must sit above the react-select menu portal (z-index 99999) so a video
     lightbox opened from an open dropdown renders on top of it. */
  z-index: 100000!important;
`;

const SecondLayerModalContainer = styled('div')`
  display: flex;
  background: white;
  border-radius: 8px;
  max-width: ${(props) => {
    if (props.activeSecondLayerModal === 'premiumFeatureUploadVideoModal') return '952px';
    if (props.activeSecondLayerModal === 'videoModal') return '720px';
    return '90%';
  }};
  max-height: 95%;
  overflow: auto;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  margin: 20px;
  box-sizing: border-box;
  ${(props) => (props.activeSecondLayerModal === 'premiumFeatureUploadVideoModal' || props.activeSecondLayerModal === 'videoModal') && 'width: 100%;'}
  ${(props) => props.activeSecondLayerModal === 'aboutExercisePropertiesModal' && `
    width: 500px;
    max-width: 95vw;
    padding: 24px;
  `}
`;

const IconContainer = styled('div')`
  position: absolute;
  top: 25px;
  right: 25px;
  display: flex;
  cursor: pointer;
    transition: ${TransitionGlobal};
    :hover {
      opacity: 0.7;
    }
`;

const SECOND_LAYER_MODAL_TYPES = [
  'addThumbnail',
  'addAssetModal',
  'premiumFeatureUploadVideoModal',
  'newQuestionForm',
  'videoModal',
  'aboutExercisePropertiesModal',
];

const SecondLayerModal = () => {
  const clickedUgradeNow = useSelector((state) => state.calendar.data.clickedUpgradeNow);
  const activeSecondLayerModal = useSelector((state) => state.calendar.data.activeSecondLayerModal);
  const secondLayerModalData = useSelector((state) => state.calendar.data.secondLayerModalData);
  const isSecondModalOpen = SECOND_LAYER_MODAL_TYPES.includes(activeSecondLayerModal);

  const dispatch = useDispatch();

  const closeModal = () => {
    /** if the user closed premium feature modal and clicked upgrade now
     * when they close the premium feature modal we are going to fetch the
     * current user again to get their udates
     */
    if (activeSecondLayerModal === 'premiumFeatureUploadVideoModal') {
      if (clickedUgradeNow) {
        /** all to get current user to see updated access level */
        dispatch(fetchCurrentUser());
      }
      dispatch(setClickedUpgradeNow(false));
    }
    dispatch(setActiveSecondLayerModal(''));
  };

  return (
    isSecondModalOpen && (
      <SecondLayerModalOverlay onClick={(e) => closeModal(e)}>
        <SecondLayerModalContainer onClick={(e) => e.stopPropagation()} activeSecondLayerModal={activeSecondLayerModal}>
          {/* AboutExercisePropertiesModal renders its own close button, so skip the wrapper's. */}
          {activeSecondLayerModal !== 'aboutExercisePropertiesModal' && (
            <IconContainer>
              <IcomoonReact
                iconSet={iconSet}
                size={14}
                icon='remove'
                onClick={() => closeModal()}
                color='#9E9E9E'
              />
            </IconContainer>
          )}
          {activeSecondLayerModal === 'addThumbnail' && (
            <AddThumbnailModalContent closeModal={closeModal} />
          )}
          {activeSecondLayerModal === 'addAssetModal' && (
            <AddAssetModalContent closeModal={closeModal} />
          )}
          {activeSecondLayerModal === 'premiumFeatureUploadVideoModal' && (
            <PremiumFeatureUploadVideoModal closeModal={closeModal} />
          )}
          {activeSecondLayerModal === 'newQuestionForm' && (
            <NewQuestionForm closeModal={closeModal} />
          )}
          {activeSecondLayerModal === 'videoModal' && secondLayerModalData?.url && (
            <VideoModal url={secondLayerModalData.url} />
          )}
          {activeSecondLayerModal === 'aboutExercisePropertiesModal' && (
            <AboutExercisePropertiesModal closeModal={closeModal} />
          )}
        </SecondLayerModalContainer>
      </SecondLayerModalOverlay>
    )
  );
};

export {
  CalendarModalHandler,
  SecondLayerModal,
};
